import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { CalendarClock, UserPlus, Radar, MessageSquare } from "lucide-react";

/* Lightweight connected-particle network on <canvas>. CSS-cheap, respects DPR, pauses on unmount. */
function ParticleField() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf, w, h, dpr;
    const N = 54;
    const nodes = [];

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    for (let i = 0; i < N; i++) {
      nodes.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.28,
        vy: (Math.random() - 0.5) * 0.28,
        r: Math.random() * 1.6 + 0.6,
      });
    }

    const LINK = 150;
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < N; i++) {
        const p = nodes[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
        for (let j = i + 1; j < N; j++) {
          const q = nodes[j];
          const dx = p.x - q.x, dy = p.y - q.y;
          const d = Math.hypot(dx, dy);
          if (d < LINK) {
            const a = (1 - d / LINK) * 0.5;
            ctx.strokeStyle = `rgba(52,211,153,${a * 0.55})`; // emerald links
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.stroke();
          }
        }
        ctx.fillStyle = "rgba(148,163,184,0.55)";
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 h-full w-full" />;
}

/* Story cards that make the product feel alive the moment you land. */
const CARDS = [
  { icon: CalendarClock, tone: "emerald", title: "Reunião do comercial", sub: "Amanhã, 15h · time avisado", pos: "left-[6%] top-[16%]", delay: 0.2, float: 14 },
  { icon: UserPlus, tone: "indigo", title: "Novo lead atribuído", sub: "Encaminhado para Fernando", pos: "left-[9%] bottom-[15%]", delay: 0.5, float: -12 },
  { icon: Radar, tone: "amber", title: "Licitação encontrada", sub: "PNCP · prazo em 5 dias", pos: "right-[7%] top-[13%]", delay: 0.35, float: -16 },
  { icon: MessageSquare, tone: "emerald", title: "Resposta no WhatsApp", sub: "ISA respondeu o cliente", pos: "right-[6%] bottom-[17%]", delay: 0.65, float: 12 },
];

const TONE = {
  emerald: "text-emerald-300 bg-emerald-400/10 ring-emerald-400/20",
  indigo: "text-indigo-300 bg-indigo-400/10 ring-indigo-400/20",
  amber: "text-amber-300 bg-amber-400/10 ring-amber-400/20",
};

function FloatingCard({ icon: Icon, tone, title, sub, pos, delay, float }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.96 }}
      animate={{ opacity: 1, y: [0, float, 0], scale: 1 }}
      transition={{
        opacity: { duration: 0.8, delay },
        scale: { duration: 0.8, delay },
        y: { duration: 7 + Math.abs(float) / 4, delay, repeat: Infinity, ease: "easeInOut" },
      }}
      className={`absolute ${pos} z-[5] hidden w-[230px] items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.06] p-3.5 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.7)] backdrop-blur-xl xl:flex`}
    >
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1 ${TONE[tone]}`}>
        <Icon size={16} strokeWidth={2} />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-[13px] font-semibold text-white/90">{title}</span>
        <span className="block truncate text-[11px] text-white/50">{sub}</span>
      </span>
    </motion.div>
  );
}

export default function AuthBackground() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden bg-[#070b16]">
      {/* deep base + vignette */}
      <div className="absolute inset-0 bg-[radial-gradient(120%_120%_at_50%_-10%,#0f1628_0%,#070b16_55%,#04060d_100%)]" />

      {/* slow aurora blobs (brand emerald + indigo + violet) */}
      <motion.div
        className="absolute -left-40 top-[-10%] h-[560px] w-[560px] rounded-full bg-emerald-500/20 blur-[130px]"
        animate={{ x: [0, 60, -20, 0], y: [0, 40, 80, 0], scale: [1, 1.12, 0.95, 1] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute -right-32 top-[8%] h-[600px] w-[600px] rounded-full bg-indigo-500/20 blur-[140px]"
        animate={{ x: [0, -50, 30, 0], y: [0, 60, 20, 0], scale: [1, 0.92, 1.1, 1] }}
        transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-[-15%] left-1/3 h-[520px] w-[520px] rounded-full bg-violet-500/15 blur-[150px]"
        animate={{ x: [0, 40, -30, 0], y: [0, -30, 20, 0], scale: [1, 1.08, 0.96, 1] }}
        transition={{ duration: 30, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* neural particle network */}
      <ParticleField />

      {/* fine grid + grain for depth */}
      <div
        className="absolute inset-0 opacity-[0.25]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.04) 1px,transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(circle at 50% 40%,black,transparent 78%)",
          WebkitMaskImage: "radial-gradient(circle at 50% 40%,black,transparent 78%)",
        }}
      />

      {/* live story cards */}
      {CARDS.map((c) => (
        <FloatingCard key={c.title} {...c} />
      ))}
    </div>
  );
}
